import type { SetScoreRequest } from '@fall26/shared'
import { useState } from 'react'
import { fmt, parseScoreText, scorePlaceholder } from './score-text.js'

/**
 * One grade, entered in place.
 *
 * Tap, type it the way it came back, Save. The preview under the box says
 * exactly what will be recorded before the tap that records it -- "47 of 50 →
 * 94%" -- because the difference between 47 points and 47 percent is the kind
 * of mistake that is invisible until the course standing is wrong.
 */
export function ScoreBox({
  current,
  maxPoints,
  busy,
  onSave,
  autoFocus = false,
}: {
  current: number | null
  maxPoints: number | null
  busy: boolean
  onSave: (body: SetScoreRequest) => void
  autoFocus?: boolean
}) {
  const [text, setText] = useState('')
  const parsed = parseScoreText(text, maxPoints)
  const dirty = text.trim() !== ''

  return (
    <form
      className="flex flex-col gap-1"
      onSubmit={(e) => {
        e.preventDefault()
        if (parsed.ok && dirty) onSave(parsed.body)
      }}
    >
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          inputMode="decimal"
          autoFocus={autoFocus}
          value={text}
          placeholder={current === null ? scorePlaceholder(maxPoints) : `${fmt(current)}%`}
          aria-label="Score"
          onChange={(e) => setText(e.target.value)}
          className="tap w-32 min-w-0 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-sunken)] px-2.5 text-[var(--text)] outline-none focus:border-[var(--accent)]"
        />
        <button
          type="submit"
          disabled={!parsed.ok || !dirty || busy}
          className="tap shrink-0 rounded-[var(--radius)] border border-[var(--text)] bg-[var(--text)] px-3 text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-inverted)] transition-opacity disabled:opacity-40"
        >
          {busy ? '…' : 'Save'}
        </button>
        {current !== null && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onSave({ pct: null })}
            className="tap shrink-0 rounded-[var(--radius)] border border-[var(--border)] px-2.5 text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]"
          >
            Clear
          </button>
        )}
      </div>
      {dirty && (
        <span className={`tabular text-[11px] ${parsed.ok ? 'text-[var(--text-subtle)]' : 'text-[var(--overdue)]'}`}>
          {parsed.ok ? parsed.preview : parsed.why}
        </span>
      )}
    </form>
  )
}
